import { insightsIntl } from "../strings/intl";
import React from "react";
// eslint-disable-next-line no-restricted-imports
import type { Moment } from "moment";
import { AutocompleteField } from "@wayflyer/flyui";

import type { CustomTimeRange, UIElement } from "../utils/types";

import { Container } from "./ui/styled";
import type { SortConfig } from "./paid-channels-summary-screen";

export enum ReferencePeriod {
  PREVIOUS_YEAR = "REFERENCE_PREVIOUS_YEAR",
  PREVIOUS_PERIOD = "REFERENCE_PREVIOUS_PERIOD",
}

const referenceStrings: Record<ReferencePeriod, { description: string; comparison: string }> = {
  [ReferencePeriod.PREVIOUS_YEAR]: {
    description: insightsIntl.formatMessage({ id: "PREVIOUS_YEAR_DESCRIPTION", defaultMessage: "Previous Year" }),
    comparison: insightsIntl.formatMessage({ id: "PREVIOUS_YEAR_COMPARISON", defaultMessage: "vs previous year" }),
  },
  [ReferencePeriod.PREVIOUS_PERIOD]: {
    description: insightsIntl.formatMessage({ id: "PREVIOUS_PERIOD_DESCRIPTION", defaultMessage: "Previous Period" }),
    comparison: insightsIntl.formatMessage({ id: "PREVIOUS_PERIOD_COMPARISON", defaultMessage: "vs previous period" }),
  },
};

export const useReferencePeriod = () => {
  const [referencePeriod, setReferencePeriod] = React.useState<ReferencePeriod>(ReferencePeriod.PREVIOUS_YEAR);
  return { referencePeriod, setReferencePeriod };
};

const descriptionForReferencePeriod = (period: ReferencePeriod) => {
  return referenceStrings[period].description;
};

export const comparisonForReferencePeriod = (period: ReferencePeriod) => {
  return referenceStrings[period].comparison;
};

const niceFormat = (time: Moment) => {
  return time.format("MMMM Do YYYY");
};

export const makePreviousPeriod = (timeRange) => {
  const span = timeRange.end.diff(timeRange.start, "day");
  return {
    start: timeRange.start.clone().subtract(span, "day"),
    end: timeRange.end.clone().subtract(span, "day"),
  };
};

export const makeAdditionalReferenceContext = (
  referencePeriod: ReferencePeriod,
  selectedTimeRange: CustomTimeRange
) => {
  if (referencePeriod === ReferencePeriod.PREVIOUS_PERIOD) {
    const previousPeriod = makePreviousPeriod(selectedTimeRange);
    return `${niceFormat(previousPeriod.start)} - ${niceFormat(previousPeriod.end)}`;
  } else {
    return null;
  }
};

export const makeReferenceHeader = (props: SortConfig): UIElement => {
  return (
    <Container style={{ minWidth: "200px" }}>
      <AutocompleteField
        name={props.name}
        placeholder={props.label}
        initialValue={props.initialValue}
        options={props.options}
        onChange={(id) => {
          const item = props.options.find((item) => item.id === id);
          if (item) {
            props.onChange(item);
          }
        }}
      />
    </Container>
  );
};

export const makeReferenceProps = (
  referencePeriod: ReferencePeriod,
  setReferencePeriod: (ReferencePeriod) => void
): SortConfig => {
  return {
    name: "reference-period",
    initialValue: referencePeriod,
    options: [
      { id: ReferencePeriod.PREVIOUS_YEAR, text: descriptionForReferencePeriod(ReferencePeriod.PREVIOUS_YEAR) },
      { id: ReferencePeriod.PREVIOUS_PERIOD, text: descriptionForReferencePeriod(ReferencePeriod.PREVIOUS_PERIOD) },
    ],
    onChange: (item) => {
      setReferencePeriod(item.id);
    },
  };
};
